import type { EventLog } from '../timeline/types/event-log';
import type { Document } from '../types/Document';
import type { Field } from '../fields/types/field';
import type { Entity } from '../types/entity';
import type { Recipient } from '../types/Recipient';
import { FieldType } from '../fields/types/enums/field-type';
import type { DocumentPage } from '../types/document-page';
import { DocumentPageType } from '../enums/document-page-type';
import { FieldSource } from '../fields/types/enums/field-source';
import { MergeTagHelper } from './merge-tag.helper';

export class MergeFieldHelper {
  public static mapMergeField(
    field: Field,
    document: Document,
    entity: Entity,
    signedEvents?: EventLog[]
  ): Field {
    const hasTagsRegex = new RegExp(/{{.*}}/);
    const recipient: Recipient =
      document.recipients.find(
        (documentRecipient: Recipient) => documentRecipient.id === field.recipientId
      ) || document.recipients[0];
    let value = '';

    if (MergeTagHelper.findTags(field.value).length) {
      value = MergeTagHelper.replaceTags(field.value, {
        field,
        recipient,
        document,
        entity,
        signedEvents,
      });
    } else if (hasTagsRegex.test(field.value)) {
      value = MergeTagHelper.stripTags(field.value);
    } else {
      field.displayProperty = MergeTagHelper.getMergeDisplayProperty(field.value);
      value = MergeTagHelper.getValue(
        field.displayProperty,
        { recipient, document, entity, field, signedEvents },
        false
      );
    }

    return {
      ...field,
      value,
    };
  }

  public static mapTextField(field: Field, document: Document, entity: Entity): Field {
    const recipient: Recipient = document.recipients.find(
      (documentRecipient: Recipient) => documentRecipient.id === field.recipientId
    );

    const value: string = MergeTagHelper.replaceTags(field.value, {
      field,
      recipient,
      document,
      entity,
    });

    return {
      ...field,
      value,
    };
  }

  static getMergeKeyInField(value: string): string | null {
    if (!value) {
      return null;
    }
    return value.split('.')[1];
  }

  static getMergeFieldsInPdf(pages: DocumentPage[]): Field[] {
    if (!pages) {
      return;
    }
    return pages
      .filter((page: DocumentPage) => page.pageType === DocumentPageType.DocumentContent)
      .flatMap(page => page.fields)
      .filter(field => field.type === FieldType.Merge);
  }

  static getRecipientAttributesForInitials(
    pages: DocumentPage[] | undefined,
    recipientId: string
  ): string[] {
    if (!pages || pages.length === 0) {
      return [];
    }

    const hasInitials = pages.find(
      page =>
        page.pageType === DocumentPageType.DocumentContent &&
        page.fields?.some(
          field =>
            field.type === FieldType.Initials &&
            field.value === 'recipient' &&
            field.recipientId === recipientId
        )
    );

    return hasInitials ? ['firstName', 'lastName'] : [];
  }

  static getDocumentMergeFieldsInPdf(fields = [] as Field[]) {
    return fields.filter(field => field.source === FieldSource.Document);
  }

  static getDocumentMergeAttributesInPdf(fields: Field[]): string[] {
    const documentMergeFields = MergeFieldHelper.getDocumentMergeFieldsInPdf(fields);

    return documentMergeFields.flatMap(field => {
      const mergeKeyInField = MergeFieldHelper.getMergeKeyInField(
        field.displayProperty || field.value
      );

      return mergeKeyInField || [];
    });
  }

  static getRecipientMergeFieldsInPdf(fields: Field[], recipientId: string): Field[] {
    if (!fields?.length || !recipientId) {
      return [];
    }

    return fields.filter(
      field => field?.recipientId == recipientId && field.source === FieldSource.Recipient
    );
  }

  static getRecipientMergeAttributesInPdf(fields: Field[], recipientId: string): string[] {
    const recipientMergeFields = MergeFieldHelper.getRecipientMergeFieldsInPdf(fields, recipientId);
    const mergeAttributes: string[] = [];

    recipientMergeFields.forEach(field => {
      const mergeKeyInField = MergeFieldHelper.getMergeKeyInField(
        field.displayProperty || field.value
      );

      if (!mergeKeyInField) {
        return [];
      }
      // in pdf fullname is not camelCase
      if (mergeKeyInField === 'fullname') {
        mergeAttributes.push('firstName', 'lastName');
        return;
      }
      mergeAttributes.push(mergeKeyInField);
    });

    return mergeAttributes;
  }
}
